import {
  collection,
  doc,
  query,
  orderBy,
  where,
  serverTimestamp,
  setDoc,
  updateDoc,
  runTransaction,
  type DocumentData,
  type FirestoreDataConverter,
  type Query,
  type WithFieldValue,
} from 'firebase/firestore';

import { firestore } from './config';
import { USE_API, api, apiQuery } from '@/services/api/client';
import { areaNombre, type AreaId } from '@/utils/areas';
import type { Solicitud, ItemSolicitud, EstadoSolicitud, TipoSolicitud } from '@/types/domain';

const converter: FirestoreDataConverter<Solicitud> = {
  toFirestore(d: WithFieldValue<Solicitud>): DocumentData {
    return d as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<Solicitud, 'id'>;
    return { id: snap.id, ...data };
  },
};

export const solicitudesCollection = collection(firestore, 'solicitudes').withConverter(converter);

export const todasSolicitudesQuery: any = USE_API
  ? apiQuery('/solicitudes', { _order: '-creada_en' })
  : query(solicitudesCollection, orderBy('creadaEn', 'desc'));

export const solicitudesActivasQuery: any = USE_API
  ? apiQuery('/solicitudes', { estado_in: 'pendiente,aprobada,comprada,entregada_parcial', _order: '-creada_en' })
  : query(
      solicitudesCollection,
      where('estado', 'in', ['pendiente', 'aprobada', 'comprada', 'entregada_parcial']),
      orderBy('creadaEn', 'desc'),
    );

// Vista admin: activas + entregadas (para el historial de entregas).
export const solicitudesAdminQuery: any = USE_API
  ? apiQuery('/solicitudes', { estado_in: 'pendiente,aprobada,comprada,entregada_parcial,entregada', _order: '-creada_en' })
  : query(
      solicitudesCollection,
      where('estado', 'in', ['pendiente', 'aprobada', 'comprada', 'entregada_parcial', 'entregada']),
      orderBy('creadaEn', 'desc'),
    );

export function solicitudesPorArea(area: AreaId | string): any {
  if (USE_API) return apiQuery('/solicitudes', { area, _order: '-creada_en' });
  return query(solicitudesCollection, where('area', '==', area), orderBy('creadaEn', 'desc'));
}

export function borradorPorArea(area: AreaId | string, tipo: TipoSolicitud = 'insumos'): any {
  if (USE_API) return apiQuery('/solicitudes', { area, tipo, estado: 'borrador' });
  return query(
    solicitudesCollection,
    where('area', '==', area),
    where('tipo', '==', tipo),
    where('estado', '==', 'borrador'),
  );
}

export function solicitudesPorAreaTipo(area: AreaId, tipo: TipoSolicitud): any {
  if (USE_API) return apiQuery('/solicitudes', { area, tipo, _order: '-creada_en' });
  return query(
    solicitudesCollection,
    where('area', '==', area),
    where('tipo', '==', tipo),
    orderBy('creadaEn', 'desc'),
  );
}

export interface CrearBorradorInput {
  area: AreaId | string;
  areaNombre?: string;
  tipo?: TipoSolicitud;
  autorUid: string;
  autorNombre: string;
  items?: ItemSolicitud[];
  nota?: string;
}

export async function crearBorrador(input: CrearBorradorInput) {
  const nombreArea = input.areaNombre ?? areaNombre(input.area);
  if (USE_API) {
    const res = await api.post('/solicitudes', {
      tipo: input.tipo ?? 'insumos',
      area: input.area,
      areaNombre: nombreArea,
      ultimoEditorUid: input.autorUid,
      ultimoEditorNombre: input.autorNombre,
      doctorUid: input.autorUid,
      doctorNombre: input.autorNombre,
      items: input.items ?? [],
      estado: 'borrador',
      nota: input.nota ?? null,
    });
    return res.id;
  }
  const id = doc(solicitudesCollection).id;
  await setDoc(doc(solicitudesCollection, id), {
    tipo: input.tipo ?? 'insumos',
    area: input.area,
    areaNombre: nombreArea,
    ultimoEditorUid: input.autorUid,
    ultimoEditorNombre: input.autorNombre,
    doctorUid: input.autorUid,
    doctorNombre: input.autorNombre,
    items: input.items ?? [],
    estado: 'borrador',
    nota: input.nota ?? null,
    creadaEn: serverTimestamp(),
    actualizadaEn: serverTimestamp(),
  } as any);
  return id;
}

export interface EditarBorradorInput {
  id: string;
  items?: ItemSolicitud[];
  nota?: string | null;
  ultimoEditorUid: string;
  ultimoEditorNombre: string;
}

export async function editarBorrador(input: EditarBorradorInput) {
  if (USE_API) {
    const body: any = {
      ultimoEditorUid: input.ultimoEditorUid,
      ultimoEditorNombre: input.ultimoEditorNombre,
      actualizadaEn: new Date().toISOString(),
    };
    if (input.items !== undefined) body.items = input.items;
    if (input.nota !== undefined) body.nota = input.nota;
    await api.patch(`/solicitudes/${input.id}`, body);
    return;
  }
  const ref = doc(solicitudesCollection, input.id);
  const update: any = {
    ultimoEditorUid: input.ultimoEditorUid,
    ultimoEditorNombre: input.ultimoEditorNombre,
    actualizadaEn: serverTimestamp(),
  };
  if (input.items !== undefined) update.items = input.items;
  if (input.nota !== undefined) update.nota = input.nota;
  await updateDoc(ref, update);
}

export async function enviarBorrador(id: string, autorUid: string, autorNombre: string) {
  // El "solicitante" (Pedido por) es quien ENVÍA la solicitud, no quien creó el
  // borrador (que puede ser otra doctora que dejó el borrador del área).
  if (USE_API) {
    await api.patch(`/solicitudes/${id}`, {
      estado: 'pendiente',
      enviadaEn: new Date().toISOString(),
      doctorUid: autorUid,
      doctorNombre: autorNombre,
      ultimoEditorUid: autorUid,
      ultimoEditorNombre: autorNombre,
    });
    return;
  }
  await updateDoc(doc(solicitudesCollection, id), {
    estado: 'pendiente',
    enviadaEn: serverTimestamp(),
    doctorUid: autorUid,
    doctorNombre: autorNombre,
    ultimoEditorUid: autorUid,
    ultimoEditorNombre: autorNombre,
  });
}

export async function eliminarSolicitud(id: string) {
  if (USE_API) {
    await api.delete(`/solicitudes/${id}`);
    return;
  }
  const { deleteDoc } = await import('firebase/firestore');
  await deleteDoc(doc(solicitudesCollection, id));
}

export async function actualizarEstadoSolicitud(id: string, estado: EstadoSolicitud, nota?: string) {
  if (USE_API) {
    const body: any = { estado };
    if (estado === 'aprobada') body.aprobadaEn = new Date().toISOString();
    if (estado === 'comprada') body.compradaEn = new Date().toISOString();
    if (estado === 'cancelada') body.canceladaEn = new Date().toISOString();
    if (nota) body.notaAdmin = nota;
    await api.patch(`/solicitudes/${id}`, body);
    return;
  }
  const ref = doc(solicitudesCollection, id);
  const update: any = { estado };
  if (estado === 'aprobada') update.aprobadaEn = serverTimestamp();
  if (estado === 'comprada') update.compradaEn = serverTimestamp();
  if (estado === 'cancelada') update.canceladaEn = serverTimestamp();
  if (nota) update.notaAdmin = nota;
  await updateDoc(ref, update);
}

export async function entregarSolicitud(
  solicitudId: string,
  entregas: Record<number, number> | null = null,
) {
  if (USE_API) {
    await api.post('/acciones/entregar-solicitud', { solicitudId, entregas });
    return;
  }
  await runTransaction(firestore, async (tx) => {
    const ref = doc(solicitudesCollection, solicitudId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('La solicitud ya no existe.');
    const sol = snap.data();
    if (sol.estado === 'entregada') return;
    if (sol.estado === 'cancelada' || sol.estado === 'rechazada' || sol.estado === 'borrador') {
      throw new Error('No se puede entregar una solicitud en este estado.');
    }
    const tipo: TipoSolicitud = sol.tipo ?? 'insumos';
    const coleccionStock = tipo === 'farmacia' ? 'productos_venta' : 'insumos_internos';
    const items: ItemSolicitud[] = sol.items ?? [];

    // Firestore exige TODOS los reads antes de cualquier write dentro de la
    // transacción. Hacemos primero todas las lecturas de stock y recién
    // después aplicamos los updates.
    const aProcesar: Array<{ idx: number; aEntregar: number; stockRef: any; stockSnap: any }> = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const yaEntregado = item.cantidadEntregada ?? 0;
      const restante = item.cantidad - yaEntregado;
      const aEntregar = entregas ? Math.max(0, Math.min(restante, entregas[i] ?? 0)) : restante;
      if (aEntregar > 0) {
        const stockRef = doc(firestore, coleccionStock, item.insumoId);
        const stockSnap = await tx.get(stockRef);
        aProcesar.push({ idx: i, aEntregar, stockRef, stockSnap });
      }
    }

    // Ahora sí, todas las escrituras.
    for (const r of aProcesar) {
      if (r.stockSnap.exists()) {
        const prod = r.stockSnap.data() as any;
        const nuevoStock = Math.max(0, (prod.stock ?? 0) - r.aEntregar);
        tx.update(r.stockRef, {
          stock: nuevoStock,
          stockBajo: nuevoStock <= (prod.stockMin ?? 0),
          updatedAt: serverTimestamp(),
        });
      }
    }

    const nuevosItems: ItemSolicitud[] = items.map((item, i) => {
      const yaEntregado = item.cantidadEntregada ?? 0;
      const restante = item.cantidad - yaEntregado;
      const aEntregar = entregas ? Math.max(0, Math.min(restante, entregas[i] ?? 0)) : restante;
      return { ...item, cantidadEntregada: yaEntregado + aEntregar };
    });

    const todoEntregado = nuevosItems.every((it) => (it.cantidadEntregada ?? 0) >= it.cantidad);
    tx.update(ref, {
      items: nuevosItems,
      estado: todoEntregado ? 'entregada' : 'entregada_parcial',
      ...(todoEntregado ? { entregadaEn: serverTimestamp() } : {}),
    });
  });
}
